// src/features/sheets/components/Spreadsheet.tsx
// Main Spreadsheet component - composes all sub-components

import React, { useRef } from "react";
import type { Sheet, CellFormat } from "../types";
import { useSpreadsheet } from "../hooks/useSpreadsheet";
import { SpreadsheetToolbar } from "./SpreadsheetToolbar";
import { SpreadsheetFormulaBar } from "./SpreadsheetFormulaBar";
import { SpreadsheetGrid } from "./SpreadsheetGrid";
import { SpreadsheetStatusBar } from "./SpreadsheetStatusBar";
import { SpreadsheetHelpPanel } from "./SpreadsheetHelpPanel";

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

export interface SpreadsheetProps {
  sheet: Sheet;
  onDataChange?: (data: string[][]) => void;
  onFormatsChange?: (formats: Record<string, CellFormat>) => void;
  onColWidthsChange?: (colWidths: number[]) => void;
  onAddRow?: (afterRow?: number) => void;
  onDeleteRow?: (row: number) => void;
  onAddColumn?: (afterCol?: number) => void;
  onDeleteColumn?: (col: number) => void;
  readOnly?: boolean;
  showToolbar?: boolean;
  showFormulaBar?: boolean;
  showStatusBar?: boolean;
  className?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const Spreadsheet: React.FC<SpreadsheetProps> = ({
  sheet,
  onDataChange,
  onFormatsChange,
  onColWidthsChange,
  onAddRow,
  onDeleteRow,
  onAddColumn,
  onDeleteColumn,
  readOnly = false,
  showToolbar = true,
  showFormulaBar = true,
  showStatusBar = true,
  className = "",
}) => {
  const [showHelp, setShowHelp] = React.useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const spreadsheet = useSpreadsheet({
    sheet,
    onDataChange,
    onFormatsChange,
    onColWidthsChange,
    readOnly,
  });

  const {
    data,
    formats,
    colWidths,
    rows,
    cols,
    selectedCell,
    editingCell,
    editValue,
    currentFormat,
    liveCache,
    isLoadingLive,
    liveCellCount,
    fillDrag,
    fillPreviewCells,
    getCellDisplay,
    handleCellClick,
    handleCellDoubleClick,
    handleEditChange,
    handleEditBlur,
    handleKeyDown,
    startEditing,
    copyCell,
    pasteCell,
    setFormat,
    handleResizeStart,
    handleFillDragStart,
    handleFillDragMove,
    fetchLiveData,
    selectedIsFormula,
  } = spreadsheet;

  // Structure operations - pass to parent if provided, otherwise no-op
  const handleAddRow = () => {
    if (onAddRow) {
      onAddRow(selectedCell?.row);
    }
  };

  const handleDeleteRow = () => {
    if (onDeleteRow && selectedCell && rows > 1) {
      onDeleteRow(selectedCell.row);
    }
  };

  const handleAddColumn = () => {
    if (onAddColumn) {
      onAddColumn(selectedCell?.col);
    }
  };

  const handleDeleteColumn = () => {
    if (onDeleteColumn && selectedCell && cols > 1) {
      onDeleteColumn(selectedCell.col);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`h-full flex flex-col bg-slate-900 focus:outline-none overflow-hidden ${className}`}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {/* Toolbar */}
      {showToolbar && (
        <SpreadsheetToolbar
          sheetName={sheet.name}
          selectedCell={selectedCell}
          currentFormat={currentFormat}
          onFormatChange={setFormat}
          onAddRow={handleAddRow}
          onDeleteRow={handleDeleteRow}
          onAddColumn={handleAddColumn}
          onDeleteColumn={handleDeleteColumn}
          onCopy={copyCell}
          onPaste={pasteCell}
          liveCellCount={liveCellCount}
          onRefreshLive={fetchLiveData}
          isLoadingLive={isLoadingLive}
          canDeleteRow={!!selectedCell && rows > 1}
          canDeleteColumn={!!selectedCell && cols > 1}
          showHelp={showHelp}
          onToggleHelp={() => setShowHelp(!showHelp)}
          readOnly={readOnly}
        />
      )}

      {/* Formula Bar */}
      {showFormulaBar && (
        <SpreadsheetFormulaBar
          selectedCell={selectedCell}
          editingCell={editingCell}
          editValue={editValue}
          cellValue={selectedCell ? data[selectedCell.row]?.[selectedCell.col] || "" : ""}
          onEditChange={handleEditChange}
          onEditFocus={startEditing}
          onEditBlur={handleEditBlur}
          onEditKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              spreadsheet.commitEdit();
              spreadsheet.setEditingCell(null);
              gridRef.current?.focus();
            } else if (e.key === "Escape") {
              spreadsheet.setEditingCell(null);
              spreadsheet.setEditValue("");
              gridRef.current?.focus();
            }
          }}
        />
      )}

      {/* Help Panel */}
      {showHelp && <SpreadsheetHelpPanel />}

      {/* Grid */}
      <SpreadsheetGrid
        ref={gridRef}
        data={data}
        formats={formats}
        colWidths={colWidths}
        rows={rows}
        cols={cols}
        selectedCell={selectedCell}
        editingCell={editingCell}
        editValue={editValue}
        fillPreviewCells={fillPreviewCells}
        fillDrag={fillDrag}
        liveCache={liveCache}
        onCellClick={handleCellClick}
        onCellDoubleClick={handleCellDoubleClick}
        onCellMouseEnter={(row, col) => {
          if (fillDrag) {
            handleFillDragMove({ row, col });
          }
        }}
        onEditChange={handleEditChange}
        onEditBlur={handleEditBlur}
        onEditKeyDown={handleKeyDown}
        onFillDragStart={handleFillDragStart}
        onResizeStart={handleResizeStart}
        getCellDisplay={getCellDisplay}
        readOnly={readOnly}
      />

      {/* Status Bar */}
      {showStatusBar && (
        <SpreadsheetStatusBar
          rows={rows}
          cols={cols}
          selectedCell={selectedCell}
          editingCell={editingCell}
          fillDrag={fillDrag}
          fillPreviewCellCount={fillPreviewCells.length}
          isFormula={selectedIsFormula}
        />
      )}
    </div>
  );
};

export default Spreadsheet;
